<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="grid-select-all">
    <PrimeCheckBox
      :model-value="allSelected"
      :binary="true"
      @update:model-value="toggleSelectAll"
    />
    <span class="grid-select-all-label">
      {{ allSelected ? $t("deselectAll") : $t("selectAll") }}
    </span>
  </div>
  <div class="grid-container" role="list" :aria-label="$t('fileList')">
    <div
      v-for="(item, index) in slotProps.items"
      :key="index"
      class="grid-item-wrapper"
      :class="{ 'grid-item-selected': isSelected(item) }"
      role="listitem"
    >
      <div class="grid-card">
        <div class="card-content">
          <div class="card-header">
            <div data-tour="item-checkbox">
              <PrimeCheckBox
                :id="item.id"
                v-model="selectedNodesCopy"
                class="checkbox-centered"
                name="option"
                :value="item"
              />
            </div>
            <div v-if="item.isLocked">
              <i
                class="ri-lock-line"
                aria-hidden="true"
                v-tooltip="alfrescoFileService().tooltipLockContent(item)"
              ></i>
            </div>
            <PrimeButton
              v-else-if="item.permissions?.isInheritanceEnabled === false"
              type="button"
              icon="ri-shield-user-line"
              severity="warn"
              text
              rounded
              size="small"
              :aria-label="$t('restrictedPermissions')"
              v-tooltip="$t('restrictedPermissions')"
              @click.stop="$emit('open-members', item)"
            />
            <router-link
              v-if="item.isFile"
              v-tooltip.top="item.name"
              :to="collaboraService.getRoute(item)"
              class="item-name-link"
              :class="[
                isCurrentNodeDisplayed(item) ? 'underline' : '',
                item.isLocked ||
                item.permissions?.isInheritanceEnabled === false
                  ? 'name-locked'
                  : 'name-normal',
              ]"
            >
              {{ item.name }}
            </router-link>
            <span
              v-else
              v-tooltip.top="item.name"
              class="item-name-link name-normal"
              @click="nodeClicked(item)"
            >
              {{ item.name }}
            </span>
            <PrimeButton
              v-tooltip.top="$t(`moreActions`)"
              :aria-label="$t('moreActions')"
              class="action-btn"
              type="button"
              icon="ri-more-2-line"
              rounded
              text
              @click="toggleFileActionMenu($event, item)"
            />
          </div>
          <div
            :key="item.id"
            class="preview-container"
            data-tour="thumbnail"
            @click="nodeClicked(item)"
          >
            <div>
              <PrimeImage
                v-if="displaySvg(item)"
                @click="openPreviewPanel(item)"
              >
                <template #image>
                  <img
                    :src="alfrescoFileService().getContentUrl(item.id)"
                    :alt="item.name"
                    class="preview-image"
                  />
                </template>
              </PrimeImage>
              <PrimeImage
                v-else-if="displayThumb(item)"
                @click="openPreviewPanel(item)"
              >
                <template #image>
                  <img
                    :src="alfrescoFileService().getPreviewUrl(item.id)"
                    :alt="item.name"
                    class="preview-image"
                    @error="onImageLoadFailure($event, item)"
                  />
                </template>
              </PrimeImage>
              <PristyIcon
                v-if="!displayThumb(item) && !displaySvg(item)"
                v-tooltip="loadIconLabel(item)"
                class="large-icon"
                :svg-file-name="loadIcon(item)"
                @click="clickIcon(item)"
              />
            </div>
          </div>
        </div>
        <div class="card-footer">
          <slot name="grid-card-extra" :item="item" />
          <div class="date-info">
            {{ $t("column.modifiedAt") }}
            {{ toLocaleDateString(item.modifiedAt) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import PristyIcon from "../PristyIcon.vue";
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import iconeService from "@/services/IconeService.js";
import Tooltip from "primevue/tooltip";
import Button from "primevue/button";
import CheckBox from "primevue/checkbox";
import Image from "primevue/image";
import formatService from "@/services/FormatService.js";

export default {
  name: "ContentGridView",
  components: {
    PristyIcon,
    PrimeButton: Button,
    PrimeCheckBox: CheckBox,
    PrimeImage: Image,
  },
  directives: {
    tooltip: Tooltip,
  },
  inject: ["collaboraService", "isCurrentNodeDisplayed"],
  props: {
    slotProps: {
      type: Object,
      default: () => ({}),
    },
    selectedNodes: {
      type: Array,
      default: () => [],
    },
    isPreviewPanelVisible: {
      type: Boolean,
      default: false,
    },
    isSmallScreen: {
      type: Boolean,
      default: false,
    },
    isInUserHome: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "reload",
    "update:selectedNodes",
    "click-icon",
    "open-preview-panel",
    "open-sharelink",
    "toggle-favorite",
    "toggle-file-action-menu",
    "open-in-new-tab",
    "node-clicked",
    "open-members",
  ],
  computed: {
    selectedNodesCopy: {
      get() {
        return this.selectedNodes;
      },
      set(value) {
        this.$emit("update:selectedNodes", value);
      },
    },
    selectedIds() {
      return new Set(this.selectedNodes.map((n) => n.id));
    },
    allSelected() {
      return (
        this.slotProps.items.length > 0 &&
        this.selectedNodes.length === this.slotProps.items.length
      );
    },
  },
  methods: {
    isSelected(item) {
      return this.selectedIds.has(item.id);
    },
    toggleSelectAll(checked) {
      this.$emit(
        "update:selectedNodes",
        checked ? [...this.slotProps.items] : [],
      );
    },
    alfrescoFileService() {
      return alfrescoFileService;
    },
    toLocaleDateString(datestr) {
      return formatService.toLocaleDateString(datestr);
    },
    displaySvg(node) {
      return node.isFile && node.content.mimeType === "image/svg+xml";
    },
    loadIcon(item) {
      return iconeService.getIcon(item);
    },
    loadIconLabel(item) {
      return iconeService.getIconLabel(item);
    },
    onImageLoadFailure(event, node) {
      alfrescoFileService.onImageLoadFailure(event, node);
    },
    displayThumb(node) {
      return alfrescoFileService.isThumbnailSupported(node);
    },
    clickIcon(node) {
      this.$emit("click-icon", node);
    },
    openPreviewPanel(node) {
      this.$emit("open-preview-panel", node);
    },
    openShareLink(node) {
      this.$emit("open-sharelink", node);
    },
    toggleFavorite(node) {
      this.$emit("toggle-favorite", node);
    },
    toggleFileActionMenu(event, node) {
      this.$emit("toggle-file-action-menu", event, node);
    },
    openInNewTab(node) {
      this.$emit("open-in-new-tab", node);
    },
    nodeClicked(node) {
      this.$emit("node-clicked", node);
    },
  },
};
</script>
<style lang="scss" scoped>
.grid-select-all {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.1rem;
}

.grid-select-all-label {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
  cursor: pointer;
  user-select: none;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
  gap: 1rem;
  padding-top: 0.5rem;
}

.grid-item-wrapper {
  padding: 0.5rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: 12px;
  transition:
    background-color 0.15s,
    border-color 0.15s;

  &:hover:not(.grid-item-selected) {
    background-color: var(--p-content-hover-background);

    .grid-card,
    .card-content,
    .card-footer {
      background-color: var(--p-content-hover-background);
    }
  }
}

.grid-item-selected {
  border-color: var(--p-primary-color);
  background-color: color-mix(
    in srgb,
    var(--p-primary-color) 22%,
    var(--p-content-background)
  );

  .grid-card,
  .card-content,
  .card-footer {
    background-color: color-mix(
      in srgb,
      var(--p-primary-color) 22%,
      var(--p-content-background)
    );
  }
}

.grid-card {
  border-radius: 10px;
  overflow: hidden;
  background-color: var(--p-datatable-row-background);
  transition: background-color 0.15s;
}

.card-content {
  background-color: var(--p-surface-ground);
  height: 12rem;
  transition: background-color 0.15s;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  height: 3rem;
  flex-wrap: nowrap;
}

.checkbox-centered {
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-name-link {
  font-size: 0.75rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0.25rem;
  color: inherit;
  text-decoration: none;
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  height: 75%;
}

.preview-container > div {
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  max-height: 9rem;
  max-width: 100%;
  object-fit: contain;
}

.large-icon {
  height: 6rem;
  width: 6rem;
  margin: auto;
  cursor: pointer;
}

.card-footer {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  transition: background-color 0.15s;
  padding: 0.25rem 0.5rem;
  background-color: var(--p-datatable-row-background);
  min-width: 0;
  overflow: hidden;
}

.date-info {
  font-size: 0.7rem;
  color: var(--p-text-muted-color);
  white-space: nowrap;
}

.underline {
  text-decoration: underline;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
